// PostHog `before_send` hook. It has two jobs, both of them about keeping the
// production project readable.
//
// 1. Non-production origins must not file into the production project. The
//    project key is hardcoded in index.jsx and is the same one every
//    environment ships with, so a developer's `yarn start`, an ngrok tunnel, a
//    Coder relay session and the staging bucket all report as production
//    traffic. Roughly a third of the project's exception events over the last
//    30 days came from developer hosts, generated by five people.
//
//    Filtering on hostname rather than build mode is deliberate: there is one
//    `vite build` for every environment, so `import.meta.env.PROD` is true on
//    staging and on any production build a developer serves through a tunnel.
//    Only the hostname distinguishes them.
//
// 2. The benign ResizeObserver loop notice is dropped everywhere, including
//    production, where it does occur.
//
// The long-term fix for (1) is to move the project key to `VITE_POSTHOG_KEY`
// sourced from the per-environment .env secret, exactly as `VITE_SENTRY_DSN`
// already is, and give staging its own project. That needs the secret to exist
// before the code ships, so it is a follow-up. This hook is the stopgap.

// Exact hostnames that are never production.
const NON_PRODUCTION_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "[::1]",
  // Staging. Errors here are already covered by a separate Sentry project
  // (nbyula-discovery-client-staging), so nothing is lost by keeping its
  // product analytics out of the production project.
  "discovery.navtest.co.in",
]);

// Suffix-matched, so a production host that merely contains one of these words
// (say ngrok-docs.nbyula.com) is not caught.
const NON_PRODUCTION_HOST_SUFFIXES = [
  ".ngrok-free.app",
  ".ngrok.io",
  ".ngrok.app",
  ".ngrok-free.dev",
  // Personal dev relay, e.g. discovery--dev--<name>.code.relay.corp.nbyula.com
  ".code.relay.corp.nbyula.com",
];

export const isNonProductionHost = (hostname) => {
  const host = String(hostname ?? "").toLowerCase();
  if (!host) return false;
  if (NON_PRODUCTION_HOSTS.has(host)) return true;
  return NON_PRODUCTION_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

// Chrome raises "ResizeObserver loop completed with undelivered notifications"
// (and the older "ResizeObserver loop limit exceeded") when resize callbacks do
// not settle inside one frame. It is a browser notice, not a failure: the page
// keeps working and the event carries no stack frames. PostHog's global error
// handler still captures it, so each notice opens a fresh error tracking issue.
const RESIZE_OBSERVER_LOOP_NOTICE = "ResizeObserver loop";

export const isResizeObserverLoopNotice = (event) => {
  if (event?.event !== "$exception") return false;
  const exceptions = event.properties?.$exception_list ?? [];
  return exceptions.some((exception) =>
    String(exception?.value ?? "").includes(RESIZE_OBSERVER_LOOP_NOTICE)
  );
};

/**
 * Build the `before_send` hook for a given origin.
 *
 * Taking the hostname as an argument rather than reading `window.location`
 * inside the hook keeps this module pure and directly testable, and means the
 * host is resolved once at init instead of on every event.
 *
 * @param {string} hostname typically `window.location.hostname`
 * @returns {(event: object|null) => object|null} the event to send, or null to drop it
 */
export const createPosthogBeforeSend = (hostname) => {
  if (isNonProductionHost(hostname)) {
    // Nothing from this origin belongs in the production project.
    return () => null;
  }
  return (event) => (isResizeObserverLoopNotice(event) ? null : event);
};
